import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { NavLink } from 'react-router-dom'
import { RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown, MessageSquare, Clock, ScanLine, ChevronRight, FileText } from 'lucide-react'
import { SowTemplateEditorPage } from './SowTemplateEditorPage'
import { SequenceDiagramToolPage } from './SequenceDiagramToolPage'
import { TestingGroundPage } from './TestingGroundPage'
import { PlatformJourneyTool } from './components/PlatformJourneyTool'

import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { api } from '@/api/client'
import type { ImplCommentHistoryEntry, ImplDealMetadata } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { showToast } from '@/store/uiStore'
import type { ImplementationIssue, SlaScanResult } from '@/api/types'
import { transformIssues } from './implementationsUtils'
import {
  STATUS_COLORS,
  STATUS_ORDER,
  SIZE_KPI_DAYS,
  RAG_CONFIG,
  durationColor,
  type SortCol,
  type SortDir,
  type RAGStatus,
} from './implementationsTypes'
import { ImplMultiSelect } from './components/ImplMultiSelect'
import { DurationBar } from './components/DurationBar'
import { TimeTrackingPanel } from './components/TimeTrackingPanel'
import { SlaImportModal } from './components/SlaImportModal'

type ImplementationsDashboardTab = 'sow' | 'inflight' | 'sequence-diagrams' | 'testing-ground' | 'platform-journey'

const tabClasses = (isActive: boolean) =>
  `px-3 py-2 text-sm rounded-t-md border border-b-0 border-[var(--border-color)] transition-colors ${
    isActive
      ? 'bg-[var(--bg-secondary)] text-[var(--text-primary)]'
      : 'bg-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
  }`

const QUARTER_YEAR = 2026
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const

const SIZE_ICONS: Record<string, string> = {
  'Small': 'S',
  'Medium': 'M',
  'Large': 'L',
  'Extra Large': 'XL',
}
type Quarter = typeof QUARTERS[number]

const HOURS_PER_DAY = 7.6

function fmtHours(h: number): string {
  if (h === 0) return '—'
  const d = h / HOURS_PER_DAY
  return d % 1 === 0 ? `${d}d` : `${d.toFixed(1)}d`
}

function formatMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleString('default', { month: 'short', year: 'numeric' })
}

function getCurrentQuarter(): Quarter {
  const month = new Date().getMonth() // 0-based
  if (month < 3) return 'Q1'
  if (month < 6) return 'Q2'
  if (month < 9) return 'Q3'
  return 'Q4'
}


export function ImplementationsDashboardPage({ tab }: { tab: ImplementationsDashboardTab }) {
  const { user } = useAuthStore()
  const [issues, setIssues] = useState<ImplementationIssue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Go-lives — derived from each initiative's Actual End (handover) date rather than
  // a separate Jira query, so manual actual-end-date overrides drive the count.
  const [goLivesQuarter, setGoLivesQuarter] = useState<Quarter>(() => getCurrentQuarter())
  const [goLivesModalOpen, setGoLivesModalOpen] = useState(false)

  // Filters
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([])
  const [sizeFilter, setSizeFilter] = useState<string[]>([])
  const [ragFilter, setRagFilter] = useState<string[]>([])
  const [goLiveMonthFilter, setGoLiveMonthFilter] = useState<string[]>([])
  const [missingDealValueFilter, setMissingDealValueFilter] = useState(false)
  const [search, setSearch] = useState('')
  const [sortCol, setSortCol] = useState<SortCol | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  // Commentary
  const [comments, setComments] = useState<Record<string, string>>({})
  const [expandedCommentKey, setExpandedCommentKey] = useState<string | null>(null)
  const [savingComment, setSavingComment] = useState<string | null>(null)
  const commentDraftRef = useRef<Record<string, string>>({})
  const [commentHistory, setCommentHistory] = useState<Record<string, ImplCommentHistoryEntry[]>>({})
  const [loadingHistory, setLoadingHistory] = useState<string | null>(null)

  // Time tracking
  const [expandedTimeKey, setExpandedTimeKey] = useState<string | null>(null)
  const [timeSummary, setTimeSummary] = useState<Record<string, { totalHours: number; entryCount: number }>>({})
  const [slaModalOpen, setSlaModalOpen] = useState(false)
  const [slaScanData, setSlaScanData] = useState<SlaScanResult | null>(null)
  const [slaScanning, setSlaScanning] = useState(false)
  const [slaImporting, setSlaImporting] = useState(false)
  const [slaImportResult, setSlaImportResult] = useState<{ initiatives: number; entries: number; totalHours: number } | null>(null)
  const [lastImportedAt, setLastImportedAt] = useState<string | null>(null)

  // Deal metadata (from DealHub presales data, loaded after issues)
  const [dealMeta, setDealMeta] = useState<Record<string, ImplDealMetadata>>({})

  // SOW PDF extraction state
  const [extractingKeys, setExtractingKeys] = useState<Set<string>>(new Set())
  const driveTokenRef = useRef<string | null>(null)
  const [editingDealValueKey, setEditingDealValueKey] = useState<string | null>(null)
  const [editDealValueInput, setEditDealValueInput] = useState('')

  const saveDealValue = async (jiraKey: string) => {
    const val = editDealValueInput.trim() === '' ? null : parseFloat(editDealValueInput.replace(/[^0-9.]/g, ''))
    setEditingDealValueKey(null)
    const res = await api.implDealMeta.saveDealValue(jiraKey, val)
    if (res.success) {
      setDealMeta(prev => ({ ...prev, [jiraKey]: { ...prev[jiraKey], dealValue: val } }))
    }
  }

  // Size bucket expansion
  const [expandedSize, setExpandedSize] = useState<string | null>(null)

  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  const fetchIssues = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.jira.getInitiatives()
      if (res.success && res.data) {
        const transformed = transformIssues(res.data.issues || [])
        setIssues(transformed)
        loadDealMeta(transformed)
      } else {
        setError(res.error || 'Failed to fetch initiatives')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const fetchTimeSummary = async () => {
    const res = await api.implTime.getSummary()
    if (res.success && res.data) setTimeSummary(res.data.summary)
  }

  const fetchLastImport = async () => {
    const res = await api.implTime.getLastImport()
    if (res.success && res.data) setLastImportedAt(res.data.importedAt)
  }

  const loadDealMeta = async (loadedIssues: ImplementationIssue[]) => {
    if (loadedIssues.length === 0) return
    const res = await api.implDealMeta.lookup(loadedIssues.map((i) => ({ key: i.key, summary: i.summary })))
    if (res.success && res.data) setDealMeta(res.data.metadata)
  }

  // Get a Google Drive OAuth access token via popup (requests drive.readonly scope)
  const getDriveToken = useCallback((): Promise<string> => {
    if (driveTokenRef.current) return Promise.resolve(driveTokenRef.current)
    return new Promise((resolve, reject) => {
      // @ts-expect-error google is loaded from CDN
      const client = window.google?.accounts?.oauth2?.initTokenClient({
        client_id: '759138193168-4p3hjiuv841k1qnsialtf1o5suqoeian.apps.googleusercontent.com',
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (resp: { access_token?: string; error?: string }) => {
          if (resp.access_token) {
            driveTokenRef.current = resp.access_token
            // Token expires in 1 hour — clear cache after 55 min
            setTimeout(() => { driveTokenRef.current = null }, 55 * 60 * 1000)
            resolve(resp.access_token)
          } else {
            reject(new Error(resp.error || 'Drive auth failed'))
          }
        },
      })
      if (!client) { reject(new Error('Google Identity Services not loaded')); return }
      client.requestAccessToken()
    })
  }, [])

  // Master MSA/contracts folder in Google Drive — customer subfolders contain MSA/reseller agreement PDFs.
  const MASTER_MSA_FOLDER_ID = '1f8OnTQpKhmrz70V7m_r4P829knKOiRse'

  // Strip company suffixes for fuzzy name matching
  const stripSuffix = (name: string) =>
    name.toLowerCase()
      .replace(/\b(pty\.?\s*ltd\.?|pty limited|limited|ltd\.?|inc\.?|llc\.?|corp\.?|co\.?)\b/g, '')
      .replace(/\s+/g, ' ').trim()

  const extractSowFromDrive = useCallback(async (jiraKey: string, customerName: string) => {
    setExtractingKeys((prev) => new Set(prev).add(jiraKey))
    try {
      const token = await getDriveToken()
      const driveHeaders = { Authorization: `Bearer ${token}` }
      const driveParams = 'supportsAllDrives=true&includeItemsFromAllDrives=true&corpora=allDrives'
      type DriveFile = { id: string; name: string; size?: string }

      const searchFiles = async (q: string): Promise<DriveFile[]> => {
        const res = await fetch(
          `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id,name,size)&pageSize=20&${driveParams}`,
          { headers: driveHeaders }
        )
        if (!res.ok) { console.error('Drive search error:', res.status, await res.text()); return [] }
        const data = await res.json() as { files?: DriveFile[] }
        return data.files || []
      }

      // Search all drives for an MSA/agreement PDF containing the customer name.
      // This avoids fragile folder-navigation and works regardless of Drive structure.
      const keyword = stripSuffix(customerName).split(' ')[0]  // e.g. "coposit"
      const base = `mimeType='application/pdf' and trashed=false`

      // 1. Best match: file has both customer name AND msa/agreement in name
      let pdfs = await searchFiles(`${base} and name contains '${keyword}' and (name contains 'msa' or name contains 'agreement' or name contains 'reseller')`)

      // 2. Any PDF with customer name in filename
      if (pdfs.length === 0) pdfs = await searchFiles(`${base} and name contains '${keyword}'`)

      // 3. Scoped to master contracts folder — any MSA/agreement PDF
      if (pdfs.length === 0) {
        pdfs = await searchFiles(`'${MASTER_MSA_FOLDER_ID}' in ancestors and ${base} and (name contains 'msa' or name contains 'agreement')`)
        pdfs = pdfs.filter((f) => f.name.toLowerCase().includes(keyword))
      }

      if (pdfs.length === 0) throw new Error(`No MSA document found for "${customerName}". Ensure the PDF filename contains the company name.`)

      // Prefer MSA/reseller agreements under 5MB (avoid large SOW decks)
      const MAX_BYTES = 5 * 1024 * 1024
      const small = pdfs.filter((f) => !f.size || parseInt(f.size) < MAX_BYTES)
      const pool = small.length > 0 ? small : pdfs
      const contractPdf =
        pool.find((f) => /msa/i.test(f.name)) ??
        pool.find((f) => /reseller|partner/i.test(f.name)) ??
        pool.find((f) => /signed|contract|agreement/i.test(f.name)) ??
        pool[0]

      const fileRes = await fetch(
        `https://www.googleapis.com/drive/v3/files/${contractPdf.id}?alt=media&supportsAllDrives=true`,
        { headers: driveHeaders }
      )
      if (!fileRes.ok) throw new Error(`Failed to download ${contractPdf.name}`)
      const pdfBuffer = await fileRes.arrayBuffer()
      // Process in chunks — spreading large Uint8Array into String.fromCharCode causes stack overflow
      const bytes = new Uint8Array(pdfBuffer)
      let binary = ''
      const CHUNK = 8192
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
      }
      const base64 = btoa(binary)

      const extractRes = await api.implDealMeta.extractSow(jiraKey, base64)
      if (extractRes.success && extractRes.data) {
        setDealMeta((prev) => ({
          ...prev,
          [jiraKey]: { ...prev[jiraKey], sowValue: extractRes.data!.sowValue },
        }))
        const val = extractRes.data.sowValue
        showToast(val != null ? `SOW extracted from ${contractPdf.name}: $${Math.round(val).toLocaleString()}` : 'No PS fee found in contract', val != null ? 'success' : 'info')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'SOW extraction failed'
      console.error('SOW extraction failed:', err)
      showToast(msg, 'error')
    } finally {
      setExtractingKeys((prev) => { const s = new Set(prev); s.delete(jiraKey); return s })
    }
  }, [getDriveToken])

  const handleSlaScan = async () => {
    setSlaScanning(true)
    setSlaScanData(null)
    setSlaImportResult(null)
    try {
      const res = await api.implTime.slaScan()
      if (res.success && res.data) setSlaScanData(res.data)
    } finally {
      setSlaScanning(false)
    }
  }

  const handleSlaImport = async () => {
    if (!slaScanData) return
    setSlaImporting(true)
    try {
      const res = await api.implTime.importSla(slaScanData)
      if (res.success && res.data) {
        setSlaImportResult(res.data.imported)
        fetchTimeSummary()
        fetchLastImport()
      }
    } finally {
      setSlaImporting(false)
    }
  }

  const openSlaModal = () => {
    setSlaModalOpen(true)
    setSlaScanData(null)
    setSlaImportResult(null)
  }

  const openGoLivesModal = () => {
    if (goLivesCount > 0) setGoLivesModalOpen(true)
  }

  const fetchComments = async () => {
    const res = await api.implBot.getComments()
    if (res.success && res.data) {
      setComments(res.data.comments)
      commentDraftRef.current = { ...res.data.comments }
    }
  }

  const loadHistory = async (key: string) => {
    if (commentHistory[key]) return
    setLoadingHistory(key)
    try {
      const res = await api.implBot.getCommentHistory(key)
      if (res.success && res.data) setCommentHistory((prev) => ({ ...prev, [key]: res.data!.history }))
    } finally {
      setLoadingHistory(null)
    }
  }

  const toggleCommentary = (key: string) => {
    if (expandedCommentKey === key) {
      setExpandedCommentKey(null)
    } else {
      setExpandedCommentKey(key)
      loadHistory(key)
    }
  }

  const saveComment = async (key: string) => {
    const text = commentDraftRef.current[key] ?? ''
    if (!text.trim()) return
    setSavingComment(key)
    try {
      await api.implBot.saveComment(key, text)
      setComments((prev) => ({ ...prev, [key]: text }))
      // Invalidate cached history so it reloads fresh next open
      setCommentHistory((prev) => { const n = { ...prev }; delete n[key]; return n })
    } finally {
      setSavingComment(null)
    }
  }

  useEffect(() => {
    fetchIssues()
    fetchComments()
    fetchLastImport()
    // Load previously-imported time on mount so the "Time Tracked" column persists
    // across page reloads. Imported time lives in the DB (impl_time_entries); without
    // this the column stays blank until the next SLA import repopulates local state.
    fetchTimeSummary()
  }, [])

  // Go-lives are the initiatives whose Actual End (handover) date falls in the selected
  // quarter. Derived from the loaded initiatives so manual actual-end-date overrides —
  // and the "Actual End" column — stay in lockstep with the go-lives count.
  const goLivesIssues = useMemo(() => {
    return issues
      .filter((i) => {
        if (!i.handoverDate) return false
        const [year, month] = i.handoverDate.split('-').map(Number)
        if (year !== QUARTER_YEAR || !month) return false
        const q: Quarter = month <= 3 ? 'Q1' : month <= 6 ? 'Q2' : month <= 9 ? 'Q3' : 'Q4'
        return q === goLivesQuarter
      })
      .sort((a, b) => (a.handoverDate || '').localeCompare(b.handoverDate || ''))
  }, [issues, goLivesQuarter])
  const goLivesCount = goLivesIssues.length

  // Derived filter options
  const assigneeOptions = useMemo(() => {
    const a = new Set(issues.map((i) => i.assignee).filter(Boolean) as string[])
    return Array.from(a).sort()
  }, [issues])

  const statusOptions = useMemo(() => {
    const s = new Set(issues.map((i) => i.status).filter(Boolean))
    return STATUS_ORDER.filter((st) => s.has(st))
  }, [issues])

  const goLiveMonthOptions = useMemo(() => {
    const months = new Set(issues.map((i) => i.handoverDate?.slice(0, 7)).filter(Boolean) as string[])
    return Array.from(months).sort()
  }, [issues])

  // Stats — use activeDays (excludes paused/waiting time) for all KPI metrics
  const maxActiveDays = useMemo(() => (issues.length ? Math.max(...issues.map((i) => i.activeDays)) : 1), [issues])
  const avgDuration = issues.length ? Math.round(issues.reduce((s, i) => s + i.activeDays, 0) / issues.length) : 0
  const longest = issues.length ? issues.reduce((a, b) => (a.activeDays > b.activeDays ? a : b)) : null
  const waitingForCustomer = issues.filter((i) => i.status === 'Waiting for Customer - External').length

  const sizeAvgDurations = useMemo(() => (
    (['Small', 'Medium', 'Large', 'Extra Large'] as const).map((size) => {
      const sizeIssues = issues.filter((i) => i.size === size)
      const avg = sizeIssues.length
        ? Math.round(sizeIssues.reduce((s, i) => s + i.activeDays, 0) / sizeIssues.length)
        : null
      return { size, avg, count: sizeIssues.length, kpi: SIZE_KPI_DAYS[size], issues: sizeIssues }
    })
  ), [issues])

  const hasActiveFilters = statusFilter.length > 0 || assigneeFilter.length > 0 || sizeFilter.length > 0 || ragFilter.length > 0 || goLiveMonthFilter.length > 0 || missingDealValueFilter || !!search

  // Filtered + sorted list
  const filtered = useMemo(() => {
    let arr = issues.filter((i) => {
      if (statusFilter.length > 0 && !statusFilter.includes(i.status)) return false
      if (assigneeFilter.length > 0 && !assigneeFilter.includes(i.assignee || '')) return false
      if (sizeFilter.length > 0 && !sizeFilter.includes(i.size || '')) return false
      if (ragFilter.length > 0 && !ragFilter.includes(i.rag || '')) return false
      if (goLiveMonthFilter.length > 0 && !goLiveMonthFilter.includes(i.handoverDate?.slice(0, 7) || '')) return false
      if (missingDealValueFilter && dealMeta[i.key]?.dealValue != null) return false
      if (search && !i.summary.toLowerCase().includes(search.toLowerCase()) && !i.key.toLowerCase().includes(search.toLowerCase())) return false
      return true
    })
    if (sortCol) {
      arr = arr.sort((a, b) => {
        let cmp = 0
        if (sortCol === 'key') cmp = a.key.localeCompare(b.key)
        else if (sortCol === 'summary') cmp = a.summary.localeCompare(b.summary)
        else if (sortCol === 'status') cmp = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
        else if (sortCol === 'shortStatus') cmp = (a.shortStatus || '').localeCompare(b.shortStatus || '')
        else if (sortCol === 'rag') {
          const order = { Red: 0, Amber: 1, Green: 2 }
          cmp = (order[a.rag as RAGStatus] ?? 3) - (order[b.rag as RAGStatus] ?? 3)
        }
        else if (sortCol === 'assignee') cmp = (a.assignee || '').localeCompare(b.assignee || '')
        else if (sortCol === 'dueDate') cmp = (a.dueDate || '').localeCompare(b.dueDate || '')
        else if (sortCol === 'handoverDate') cmp = (a.handoverDate || '').localeCompare(b.handoverDate || '')
        else if (sortCol === 'created') cmp = (a.created || '').localeCompare(b.created || '')
        else if (sortCol === 'days') cmp = a.activeDays - b.activeDays
        return sortDir === 'asc' ? cmp : -cmp
      })
    }
    return arr
  }, [issues, statusFilter, assigneeFilter, sizeFilter, goLiveMonthFilter, missingDealValueFilter, dealMeta, search, sortCol, sortDir])

  const clearAllFilters = () => {
    setStatusFilter([])
    setAssigneeFilter([])
    setSizeFilter([])
    setRagFilter([])
    setGoLiveMonthFilter([])
    setMissingDealValueFilter(false)
    setSearch('')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Implementations</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Implementation team workspace
          </p>
        </div>
        {tab === 'inflight' && (
          <div className="flex items-center gap-2 flex-wrap">
            {lastImportedAt && (
              <span className="text-[11px] text-[var(--text-muted)]">
                Jira import: {new Date(lastImportedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={openSlaModal}>
              <ScanLine className="h-4 w-4 mr-1" />
              Import Jira SLA
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchIssues} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 border-b border-[var(--border-color)] overflow-x-auto scrollbar-none">
        <NavLink to="/dashboard/implementations/inflight" className={({ isActive }) => tabClasses(isActive)}>
          In-flight Initiatives
        </NavLink>
        <NavLink to="/dashboard/implementations/sow" className={({ isActive }) => tabClasses(isActive)}>
          SOW
        </NavLink>
        <NavLink to="/dashboard/implementations/sequence-diagrams" className={({ isActive }) => tabClasses(isActive)}>
          Sequence Diagrams
        </NavLink>
        <NavLink to="/dashboard/implementations/testing-ground" className={({ isActive }) => tabClasses(isActive)}>
          Testing Ground
        </NavLink>
        <NavLink to="/dashboard/implementations/platform-journey" className={({ isActive }) => tabClasses(isActive)}>
          Platform Journey Prototype
        </NavLink>
      </div>

      {slaModalOpen && (
        <SlaImportModal
          scanData={slaScanData}
          scanning={slaScanning}
          importing={slaImporting}
          importResult={slaImportResult}
          onScan={handleSlaScan}
          onImport={handleSlaImport}
          onClose={() => setSlaModalOpen(false)}
        />
      )}

      {tab === 'sow' && <SowTemplateEditorPage />}

      {tab === 'sequence-diagrams' && <SequenceDiagramToolPage />}

      {tab === 'testing-ground' && <TestingGroundPage />}

      {tab === 'platform-journey' && <PlatformJourneyTool />}


      {tab === 'inflight' && (
        <>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">
              {error} &mdash;{' '}
              <button onClick={fetchIssues} className="text-red-400 underline cursor-pointer bg-transparent border-none">
                retry
              </button>
            </div>
          )}

          {loading && issues.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-[var(--text-muted)]">Loading live data from Jira...</span>
            </div>
          ) : !loading && issues.length === 0 && !error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-4xl mb-4">📭</div>
              <p className="text-[var(--text-primary)] font-semibold mb-1">No initiatives found</p>
              <p className="text-[var(--text-muted)] text-sm max-w-md">
                The Jira query returned 0 results. This usually means the{' '}
                <code className="bg-[var(--bg-primary)] px-1 rounded text-xs">IMPL_JIRA_EMAIL</code> and{' '}
                <code className="bg-[var(--bg-primary)] px-1 rounded text-xs">IMPL_JIRA_API_TOKEN</code>{' '}
                Cloudflare secrets are not configured, or the account has no access to the IMPL project.
              </p>
            </div>
          ) : issues.length > 0 && (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                  { label: 'Total Initiatives', value: issues.length.toString(), sub: 'Active customers', color: '#6366f1' },
                  { label: 'Avg. Active Duration', value: `${avgDuration}d`, sub: 'Excludes waiting time', color: '#f59e0b' },
                  { label: 'Longest Running', value: longest ? `${longest.activeDays}d` : '—', sub: longest?.summary || '', color: '#ef4444' },
                  { label: 'Waiting for Customer - External', value: waitingForCustomer.toString(), sub: 'Held up on customer action', color: '#f97316' },
                ].map((k) => (
                  <Card key={k.label} className="p-5 border-t-[3px]" style={{ borderTopColor: k.color }}>
                    <div className="text-[28px] font-bold" style={{ color: k.color }}>{k.value}</div>
                    <div className="text-[13px] font-semibold text-[var(--text-primary)] mt-1">{k.label}</div>
                    <div className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">{k.sub}</div>
                  </Card>
                ))}

                {/* Go-lives card with quarter selector */}
                <Card className="p-5 border-t-[3px]" style={{ borderTopColor: '#10b981' }}>
                  <button
                    onClick={openGoLivesModal}
                    disabled={goLivesCount === 0}
                    className="text-[28px] font-bold bg-transparent border-none p-0 cursor-pointer disabled:cursor-default transition-opacity hover:opacity-80 disabled:opacity-100"
                    style={{ color: '#10b981' }}
                    title={goLivesCount > 0 ? `View ${goLivesCount} go-live${goLivesCount !== 1 ? 's' : ''}` : undefined}
                  >
                    {goLivesCount}
                  </button>
                  <button
                    onClick={openGoLivesModal}
                    disabled={goLivesCount === 0}
                    className="block text-left text-[13px] font-semibold text-[var(--text-primary)] mt-1 bg-transparent border-none p-0 cursor-pointer disabled:cursor-default"
                    title={goLivesCount > 0 ? `View ${goLivesCount} go-live${goLivesCount !== 1 ? 's' : ''}` : undefined}
                  >
                    Go-lives
                    {goLivesCount > 0 && (
                      <span className="ml-1.5 text-[10px] font-normal text-[#10b981] opacity-70">click to view</span>
                    )}
                  </button>
                  <select
                    value={goLivesQuarter}
                    onChange={(e) => setGoLivesQuarter(e.target.value as Quarter)}
                    className="mt-1.5 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-1.5 py-0.5 text-[11px] text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#10b981] cursor-pointer"
                  >
                    {QUARTERS.map((q) => (
                      <option key={q} value={q}>{q} {QUARTER_YEAR}</option>
                    ))}
                  </select>
                </Card>
              </div>

              {/* Per-size avg active duration */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {sizeAvgDurations.map(({ size, avg, count, kpi, issues: sizeIssues }) => {
                  const color = avg !== null ? durationColor(avg, kpi) : '#6B7280'
                  const isOpen = expandedSize === size
                  return (
                    <div key={size}>
                      <Card
                        className="p-4 border-t-[3px] cursor-pointer hover:opacity-90 transition-opacity select-none"
                        style={{ borderTopColor: color }}
                        onClick={() => setExpandedSize(isOpen ? null : size)}
                      >
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[24px] font-bold" style={{ color }}>
                            {avg !== null ? `${avg}d` : '—'}
                          </span>
                          {avg !== null && (
                            <span className="text-[11px] text-[var(--text-muted)]">/ {kpi}d KPI</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <div className="text-[12px] font-semibold text-[var(--text-primary)]">
                            {SIZE_ICONS[size] ?? '▪'} {size}
                          </div>
                          {count > 0 && (
                            <ChevronRight size={12} className={`text-[var(--text-muted)] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          )}
                        </div>
                        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                          {count > 0 ? `${count} initiative${count !== 1 ? 's' : ''}` : 'No data'}
                        </div>
                      </Card>
                      {isOpen && count > 0 && (
                        <div className="mt-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
                          {sizeIssues.map((si) => (
                            <div key={si.key} className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--border-color)] last:border-b-0 text-[12px]">
                              <a
                                href={`https://frankieone.atlassian.net/browse/${si.key}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary font-semibold no-underline hover:underline shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {si.key}
                              </a>
                              <span className="text-[var(--text-muted)] truncate">{si.summary}</span>
                              <span className="ml-auto shrink-0 font-mono text-[11px]" style={{ color: durationColor(si.activeDays, kpi) }}>
                                {si.activeDays}d
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Filters */}
              <Card className="p-4">
                <div className="flex gap-3 flex-wrap items-center">
                  <input
                    placeholder="Search by name or key..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 min-w-[180px] bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-[var(--text-primary)] text-[13px] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <ImplMultiSelect
                    label="Status"
                    options={statusOptions}
                    selected={statusFilter}
                    onChange={setStatusFilter}
                    renderOption={(opt) => (
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                          style={{ background: STATUS_COLORS[opt] || '#6B7280' }}
                        />
                        {opt}
                      </span>
                    )}
                  />
                  <ImplMultiSelect
                    label="Size"
                    options={['Small', 'Medium', 'Large', 'Extra Large']}
                    selected={sizeFilter}
                    onChange={setSizeFilter}
                    renderOption={(opt) => (
                      <span className="flex items-center gap-2">
                        <span>{SIZE_ICONS[opt] ?? '▪'}</span>
                        <span>{opt}</span>
                      </span>
                    )}
                  />
                  <ImplMultiSelect
                    label="RAG"
                    options={['Red', 'Amber', 'Green']}
                    selected={ragFilter}
                    onChange={setRagFilter}
                    renderOption={(opt) => {
                      const cfg = RAG_CONFIG[opt as RAGStatus]
                      return (
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: cfg?.color }} />
                          {opt}
                        </span>
                      )
                    }}
                  />
                  <ImplMultiSelect
                    label="Assignee"
                    options={assigneeOptions}
                    selected={assigneeFilter}
                    onChange={setAssigneeFilter}
                  />
                  {goLiveMonthOptions.length > 0 && (
                    <ImplMultiSelect
                      label="Go-live Month"
                      options={goLiveMonthOptions}
                      selected={goLiveMonthFilter}
                      onChange={setGoLiveMonthFilter}
                      renderOption={(opt) => formatMonth(opt)}
                    />
                  )}
                  <button
                    onClick={() => setMissingDealValueFilter((v) => !v)}
                    className={`flex items-center gap-1.5 text-[12px] rounded-md px-2.5 py-1.5 border transition-colors cursor-pointer ${
                      missingDealValueFilter
                        ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
                        : 'bg-transparent border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                    }`}
                    title="Show only initiatives missing a deal value"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full inline-block ${missingDealValueFilter ? 'bg-amber-400' : 'bg-[var(--text-muted)]'}`} />
                    Missing Deal Value
                  </button>
                  <span className="text-[var(--text-muted)] text-[13px]">{filtered.length} shown</span>

                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-2.5 py-1 cursor-pointer hover:bg-red-500/20 transition-colors"
                    >
                      ✕ Clear filters
                    </button>
                  )}
                </div>

                {/* Active filter chips */}
                {hasActiveFilters && (
                  <div className="flex gap-1.5 flex-wrap mt-2.5">
                    {statusFilter.map((s) => (
                      <span
                        key={s}
                        onClick={() => setStatusFilter(statusFilter.filter((v) => v !== s))}
                        className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1"
                        style={{
                          background: (STATUS_COLORS[s] || '#6B7280') + '22',
                          color: STATUS_COLORS[s] || '#6B7280',
                          border: `1px solid ${(STATUS_COLORS[s] || '#6B7280')}44`,
                        }}
                      >
                        {s} ✕
                      </span>
                    ))}
                    {sizeFilter.map((s) => (
                      <span
                        key={s}
                        onClick={() => setSizeFilter(sizeFilter.filter((v) => v !== s))}
                        className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1 bg-violet-500/10 text-violet-400 border border-violet-500/20"
                      >
                        {SIZE_ICONS[s] ?? '▪'} {s} ✕
                      </span>
                    ))}
                    {ragFilter.map((r) => {
                      const cfg = RAG_CONFIG[r as RAGStatus]
                      return (
                        <span
                          key={r}
                          onClick={() => setRagFilter(ragFilter.filter((v) => v !== r))}
                          className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1"
                          style={{ background: cfg?.bg, color: cfg?.color, border: `1px solid ${cfg?.border}` }}
                        >
                          {r} ✕
                        </span>
                      )
                    })}
                    {assigneeFilter.map((a) => (
                      <span
                        key={a}
                        onClick={() => setAssigneeFilter(assigneeFilter.filter((v) => v !== a))}
                        className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1 bg-primary/10 text-primary border border-primary/20"
                      >
                        {a} ✕
                      </span>
                    ))}
                    {goLiveMonthFilter.map((m) => (
                      <span
                        key={m}
                        onClick={() => setGoLiveMonthFilter(goLiveMonthFilter.filter((v) => v !== m))}
                        className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                      >
                        {formatMonth(m)} ✕
                      </span>
                    ))}
                    {missingDealValueFilter && (
                      <span
                        onClick={() => setMissingDealValueFilter(false)}
                        className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1 bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      >
                        Missing Deal Value ✕
                      </span>
                    )}
                    {search && (
                      <span
                        onClick={() => setSearch('')}
                        className="text-[11px] rounded-full px-2.5 py-0.5 cursor-pointer flex items-center gap-1 bg-[var(--bg-primary)] text-[var(--text-muted)] border border-[var(--border-color)]"
                      >
                        &ldquo;{search}&rdquo; ✕
                      </span>
                    )}
                  </div>
                )}
              </Card>

              {/* Go-lives modal */}
              {goLivesModalOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                  onClick={() => setGoLivesModalOpen(false)}
                >
                  <div
                    className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
                      <div>
                        <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                          Go-lives — {goLivesQuarter} {QUARTER_YEAR}
                        </h2>
                        <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
                          {goLivesIssues.length} initiative{goLivesIssues.length !== 1 ? 's' : ''} went live this quarter
                        </p>
                      </div>
                      <button
                        onClick={() => setGoLivesModalOpen(false)}
                        className="text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer text-lg leading-none p-1"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-[60vh]">
                      {goLivesIssues.length === 0 ? (
                        <div className="py-10 text-center text-[var(--text-muted)] text-sm">
                          No go-lives found for {goLivesQuarter} {QUARTER_YEAR}
                        </div>
                      ) : (
                        <table className="w-full text-sm border-collapse">
                          <thead className="sticky top-0 bg-[var(--bg-primary)]">
                            <tr>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">Key</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">Initiative</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">Assignee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {goLivesIssues.map((issue, idx) => (
                              <tr
                                key={issue.key}
                                className={`border-t border-[var(--bg-primary)] ${idx % 2 === 0 ? 'bg-[var(--bg-secondary)]' : 'bg-[var(--bg-secondary)]/70'}`}
                              >
                                <td className="px-4 py-2.5 whitespace-nowrap">
                                  <a
                                    href={`https://frankieone.atlassian.net/browse/${issue.key}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[#10b981] font-semibold no-underline hover:underline"
                                  >
                                    {issue.key}
                                  </a>
                                </td>
                                <td className="px-4 py-2.5 text-[var(--text-primary)]">{issue.summary}</td>
                                <td className="px-4 py-2.5 text-[var(--text-muted)] whitespace-nowrap">
                                  {issue.assignee || <span className="opacity-40">—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    <div className="px-5 py-3 border-t border-[var(--border-color)] flex justify-between items-center">
                      <div className="flex gap-2">
                        {QUARTERS.map((q) => (
                          <button
                            key={q}
                            onClick={() => setGoLivesQuarter(q)}
                            className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors cursor-pointer ${
                              q === goLivesQuarter
                                ? 'bg-[#10b981]/20 border-[#10b981]/40 text-[#10b981] font-semibold'
                                : 'bg-transparent border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                            }`}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setGoLivesModalOpen(false)}
                        className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Data table */}
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-primary)]">
                      {([
                        { label: 'Key', col: 'key' },
                        { label: 'Initiative', col: 'summary' },
                        { label: 'Status', col: 'status' },
                        { label: 'Short Status', col: 'shortStatus' },
                        { label: 'RAG', col: 'rag' },
                        { label: 'Assignee', col: 'assignee' },
                        { label: 'Due Date', col: 'dueDate' },
                        { label: 'Actual End', col: 'handoverDate' },
                        { label: 'Created', col: 'created' },
                        { label: 'Active Duration', col: 'days' },
                      ] as { label: string; col: SortCol }[]).map(({ label, col }) => (
                        <th
                          key={col}
                          onClick={() => handleSort(col)}
                          className={`px-3.5 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)] cursor-pointer select-none hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-primary)] ${
                            col === 'key' ? 'sticky left-0 z-20 w-[110px] min-w-[110px]' :
                            col === 'summary' ? 'sticky left-[110px] z-20 border-r border-[var(--border-color)]' : ''
                          }`}
                        >
                          <span className="flex items-center gap-1">
                            {label}
                            {sortCol === col
                              ? sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                              : <ChevronsUpDown size={12} className="opacity-30" />}
                          </span>
                        </th>
                      ))}
                      <th className="px-3.5 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">Deal Type</th>
                      <th className="px-3.5 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">Deal Value</th>
                      <th className="px-3.5 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">SOW Value</th>
                      <th className="px-3.5 py-2.5 text-left text-xs font-semibold text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock size={11} />
                          Time Tracked
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((i, idx) => {
                      const ragCfg = i.rag ? RAG_CONFIG[i.rag] : null
                      const needsCommentary = i.rag === 'Red' || i.rag === 'Amber'
                      const isExpanded = expandedCommentKey === i.key
                      const hasComment = !!(comments[i.key] || '').trim()
                      const rowBg = idx % 2 === 0 ? 'bg-[var(--bg-secondary)]' : 'bg-[var(--bg-secondary)]/70'
                      return (
                        <React.Fragment key={i.key}>
                          <tr className={`border-t border-[var(--bg-primary)] ${rowBg}`}>
                            <td className="px-3.5 py-2.5 whitespace-nowrap sticky left-0 z-10 w-[110px] min-w-[110px] bg-[var(--bg-secondary)]">
                              <a
                                href={`https://frankieone.atlassian.net/browse/${i.key}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary font-semibold no-underline hover:underline"
                              >
                                {i.key}
                              </a>
                            </td>
                            <td className="px-3.5 py-2.5 text-[var(--text-primary)] max-w-[180px] sticky left-[110px] z-10 border-r border-[var(--border-color)] bg-[var(--bg-secondary)]">{i.summary}</td>
                            <td className="px-3.5 py-2.5">
                              <span
                                className="rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
                                style={{
                                  background: (STATUS_COLORS[i.status] || '#6B7280') + '22',
                                  color: STATUS_COLORS[i.status] || '#6B7280',
                                  border: `1px solid ${(STATUS_COLORS[i.status] || '#6B7280')}44`,
                                }}
                              >
                                {i.status}
                              </span>
                            </td>
                            <td className="px-3.5 py-2.5 text-[var(--text-muted)] max-w-[220px]">
                              {i.shortStatus
                                ? <span className="text-xs text-[var(--text-primary)] line-clamp-2" title={i.shortStatus}>{i.shortStatus}</span>
                                : <span className="text-[var(--text-muted)]/40">—</span>}
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              {ragCfg ? (
                                <button
                                  onClick={() => needsCommentary ? toggleCommentary(i.key) : undefined}
                                  className="flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold border transition-colors"
                                  style={{ background: ragCfg.bg, color: ragCfg.color, border: `1px solid ${ragCfg.border}`, cursor: needsCommentary ? 'pointer' : 'default' }}
                                  title={needsCommentary ? (isExpanded ? 'Collapse commentary' : 'Add / view commentary') : undefined}
                                >
                                  {i.rag}
                                  {needsCommentary && (
                                    <MessageSquare size={10} className={hasComment ? 'opacity-100' : 'opacity-40'} />
                                  )}
                                </button>
                              ) : (
                                <span className="text-[var(--text-muted)]/40 text-[11px]">—</span>
                              )}
                            </td>
                            <td className="px-3.5 py-2.5 text-[var(--text-muted)] whitespace-nowrap">
                              {i.assignee || <span className="text-[var(--text-muted)]/50">Unassigned</span>}
                            </td>
                            <td className="px-3.5 py-2.5 text-[var(--text-muted)] whitespace-nowrap">
                              {i.dueDate ?? <span className="text-[var(--text-muted)]/40">—</span>}
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              {i.handoverDate
                                ? <span className="text-emerald-500">{i.handoverDate}</span>
                                : <span className="text-[var(--text-muted)]/40">—</span>}
                            </td>
                            <td className="px-3.5 py-2.5 text-[var(--text-muted)] whitespace-nowrap">{i.created}</td>
                            <td className="px-3.5 py-2.5 min-w-[180px]">
                              <DurationBar
                                days={i.activeDays}
                                max={maxActiveDays}
                                waitingDays={i.waitingDays}
                                size={i.size}
                                clockStopped={i.status === 'Transitions to Support / CS'}
                                handoverDate={i.handoverDate}
                              />
                            </td>
                            {/* Deal Type / Deal Value / SOW Value — read-only, sourced from HubSpot sync */}
                            {(() => {
                              const meta = dealMeta[i.key]
                              const dt = meta?.dealType
                              const dtColors: Record<string, string> = { new: '#10b981', existing: '#6366f1', upsell: '#6366f1', resell: '#f59e0b' }
                              return (
                                <>
                                  <td className="px-3.5 py-2.5 whitespace-nowrap">
                                    {dt ? (
                                      <span
                                        className="text-[11px] rounded-md px-2 py-0.5 border capitalize"
                                        style={{ background: (dtColors[dt] ?? '#6B7280') + '18', color: dtColors[dt] ?? '#6B7280', border: `1px solid ${(dtColors[dt] ?? '#6B7280')}30` }}
                                      >
                                        {dt}
                                      </span>
                                    ) : (
                                      <span className="text-[var(--text-muted)]/40 text-[11px]">—</span>
                                    )}
                                  </td>
                                  <td className="px-3.5 py-2.5 whitespace-nowrap text-[11px] text-[var(--text-muted)]">
                                    {editingDealValueKey === i.key ? (
                                      <input
                                        autoFocus
                                        type="text"
                                        value={editDealValueInput}
                                        onChange={e => setEditDealValueInput(e.target.value)}
                                        onBlur={() => saveDealValue(i.key)}
                                        onKeyDown={e => { if (e.key === 'Enter') saveDealValue(i.key); if (e.key === 'Escape') setEditingDealValueKey(null) }}
                                        placeholder="e.g. 130000"
                                        className="w-24 bg-[var(--bg-secondary)] border border-[#6366f1] rounded px-1.5 py-0.5 text-[11px] text-[var(--text-primary)] outline-none"
                                      />
                                    ) : (
                                      <button
                                        onClick={() => { setEditingDealValueKey(i.key); setEditDealValueInput(meta?.dealValue != null ? String(Math.round(meta.dealValue)) : '') }}
                                        className="cursor-pointer hover:text-[var(--text-primary)] transition-colors bg-transparent border-none text-left w-full flex items-center gap-1.5"
                                        title="Click to edit deal value"
                                      >
                                        {meta?.dealValue != null
                                          ? `$${Math.round(meta.dealValue).toLocaleString()}`
                                          : (
                                            <>
                                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block shrink-0" title="Deal value missing" />
                                              <span className="text-amber-400/70">Add value</span>
                                            </>
                                          )
                                        }
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-3.5 py-2.5 whitespace-nowrap text-[11px] text-[var(--text-muted)]">
                                    <div className="flex items-center gap-1.5">
                                      {meta?.sowValue != null
                                        ? `$${Math.round(meta.sowValue).toLocaleString()}`
                                        : <span className="opacity-40">—</span>}
                                      {meta?.matchedCustomer && (
                                        <button
                                          onClick={() => extractSowFromDrive(i.key, meta.matchedCustomer!)}
                                          disabled={extractingKeys.has(i.key)}
                                          className="flex items-center gap-0.5 text-[10px] text-[#6366f1] opacity-50 hover:opacity-100 bg-transparent border-none cursor-pointer disabled:cursor-wait shrink-0"
                                          title="Extract SOW from contract PDF"
                                        >
                                          <FileText size={10} />
                                          {extractingKeys.has(i.key) ? 'Extracting…' : ''}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </>
                              )
                            })()}
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              {(() => {
                                const summary = timeSummary[i.key]
                                const totalHours = summary?.totalHours ?? 0
                                const isOpen = expandedTimeKey === i.key
                                return (
                                  <button
                                    onClick={() => setExpandedTimeKey(isOpen ? null : i.key)}
                                    className="flex items-center gap-1.5 text-[11px] rounded-md px-2 py-0.5 border transition-colors cursor-pointer"
                                    style={totalHours > 0
                                      ? { background: '#6366f1' + '18', color: '#6366f1', border: '1px solid #6366f130' }
                                      : { background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-color)', opacity: 0.6 }
                                    }
                                    title={isOpen ? 'Hide time tracking' : 'View / log time'}
                                  >
                                    <Clock size={10} />
                                    {fmtHours(totalHours)}
                                  </button>
                                )
                              })()}
                            </td>
                          </tr>
                          {expandedTimeKey === i.key && (
                            <tr className={`${rowBg} border-t-0`}>
                              <td colSpan={12} className="px-4 pb-3 pt-0">
                                <TimeTrackingPanel
                                  jiraKey={i.key}
                                  jiraTimeSpentSeconds={i.jiraTimeSpent}
                                  currentUserEmail={user?.email ?? ''}
                                  currentUserName={user?.name ?? null}
                                />
                              </td>
                            </tr>
                          )}
                          {needsCommentary && isExpanded && ragCfg && (
                            <tr className={`${rowBg} border-t-0`}>
                              <td colSpan={12} className="px-4 pb-3 pt-0">
                                <div
                                  className="rounded-lg p-3 border"
                                  style={{ background: ragCfg.bg, borderColor: ragCfg.border }}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[11px] font-semibold" style={{ color: ragCfg.color }}>
                                      {i.rag} — Commentary
                                    </span>
                                    <span className="text-[11px] text-[var(--text-muted)]">
                                      Explain why this initiative is {i.rag?.toLowerCase()} and what actions are being taken.
                                    </span>
                                  </div>
                                  <textarea
                                    className="w-full bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:ring-1"
                                    style={{ '--tw-ring-color': ragCfg.color } as React.CSSProperties}
                                    rows={3}
                                    placeholder="Add commentary for this initiative..."
                                    defaultValue={comments[i.key] || ''}
                                    onChange={(e) => { commentDraftRef.current[i.key] = e.target.value }}
                                    onBlur={() => saveComment(i.key)}
                                  />
                                  <div className="flex items-center justify-between mt-2">
                                    <span className="text-[11px] text-[var(--text-muted)]">
                                      {savingComment === i.key ? 'Saving...' : hasComment ? 'Saved' : 'Auto-saves on focus loss'}
                                    </span>
                                    <button
                                      onClick={() => saveComment(i.key)}
                                      disabled={savingComment === i.key}
                                      className="text-[11px] px-2.5 py-1 rounded-md border font-medium transition-colors disabled:opacity-50"
                                      style={{ color: ragCfg.color, borderColor: ragCfg.border, background: ragCfg.bg }}
                                    >
                                      {savingComment === i.key ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>

                                  {/* History */}
                                  {loadingHistory === i.key ? (
                                    <div className="mt-3 text-[11px] text-[var(--text-muted)]">Loading history...</div>
                                  ) : commentHistory[i.key] && commentHistory[i.key].length > 0 && (
                                    <div className="mt-3 border-t pt-3" style={{ borderColor: ragCfg.border }}>
                                      <div className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2">
                                        Previous comments ({commentHistory[i.key].length})
                                      </div>
                                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                        {commentHistory[i.key].map((entry) => (
                                          <div key={entry.id} className="rounded-md px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)]">
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="text-[11px] font-medium text-[var(--text-primary)]">
                                                {entry.author_name || entry.author_email || 'Unknown'}
                                              </span>
                                              <span className="text-[10px] text-[var(--text-muted)]">
                                                {new Date(entry.created_at + 'Z').toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                              </span>
                                            </div>
                                            <p className="text-[12px] text-[var(--text-muted)] whitespace-pre-wrap">{entry.comment}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div className="text-center py-8 text-[var(--text-muted)]">No initiatives match your filters.</div>
                )}
                </div>
              </Card>
            </>
          )}
      </>
      )}
    </div>
  )
}
